import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Injector,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChildren
} from '@angular/core';
import { ButtonComponent, DialogService, SnackbarService, StepperComponent } from '@siemens/component-lib';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    catchError,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    Observable,
    of,
    pairwise,
    Subject,
    switchMap,
    takeUntil,
    tap
} from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SectionType } from '../../../shared/enums/StepperSectionType.enum';
import {
    CreateWorkflowData,
    WorkflowConfig,
    WorkflowConfigStep,
    WorkflowData,
    WorkflowDownloadProgress,
    WorkflowDownloadStatus,
    WorkflowValidationStatus
} from '@monorepo/interfaces';
import { Store } from '@ngxs/store';
import { WorkflowDataState } from '../../../shared/state/workflow/workflow-data.state';
import { WorkflowStepperSkeletonComponent } from './workflow-stepper-skeleton/workflow-stepper-skeleton.component';
import { WorkflowContentSkeletonComponent } from './workflow-content-skeleton/workflow-content-skeleton.component';
import { WorkflowStepFieldsComponent } from '../../../shared/components/workflow-step-fields/workflow-step-fields.component';
import { WorkflowConfigState } from '../../../shared/state/workflow/workflow-config.state';
import { Step } from '../../../shared/interfaces/workflows/StepperStep';
import { WorkflowFormService } from '../../../shared/services/workflow-form.service';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { GetSectionFormPipe } from '../../../shared/pipes/get-section-form.pipe';
import { GetStepFormPipe } from '../../../shared/pipes/get-step-form.pipe';
import { CreateWorkflowOperations } from '../../create-workflow/create-workflow.operations';
import {
    ClearValidationJob,
    CreateNewWorkflowData,
    CreateNewWorkflowDataFromSapSearch,
    SendWorkflowToNextStep,
    StartValidationJob,
    StartWorkflowDownload,
    UpdateWorkflowData
} from '../../../shared/state/workflow/workflow-data.actions';
import { WorkflowOperationType } from '../../../shared/enums/WorkflowOperationType.enum';
import { AuthService } from '../../../auth/services/auth.service';
import { MessageOperationType } from '../../../shared/enums/MessageOperationType.enum';
import { WorkflowOperations } from './workflow.operations';
import { UserRole } from '../../../shared/enums/UserRole.enum';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { WorkflowTransitionStepperComponent } from '../../../shared/components/workflow-transition-stepper/workflow-transition-stepper.component';
import { WorkflowTransitionPhase } from '../../../shared/components/workflow-transition-stepper/workflow-transition-phase.enum';
import { OneTimerService } from '../../../shared/services/one-timer.service';

@Component({
    selector: 'app-workflow',
    imports: [
        StepperComponent,
        ButtonComponent,
        TranslatePipe,
        WorkflowStepperSkeletonComponent,
        WorkflowContentSkeletonComponent,
        WorkflowStepFieldsComponent,
        GetStepFormPipe,
        GetStepFormPipe,
        GetStepFormPipe,
        GetSectionFormPipe,
        GetStepFormPipe,
        LoadingIndicatorComponent,
        WorkflowTransitionStepperComponent
    ],
    templateUrl: './workflow.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private injector = inject(Injector);
    private store = inject(Store);
    private workflowFormService = inject(WorkflowFormService);
    private snackbarService = inject(SnackbarService);
    private toastService = inject(SiToastNotificationService);
    private translateService = inject(TranslateService);
    private dialogService = inject(DialogService);
    private authService = inject(AuthService);
    private workflowOperations = inject(WorkflowOperations);
    private readonly idleTimer = inject(OneTimerService).createTimer(1000);
    private readonly navigationTimer = inject(OneTimerService).createTimer(1000);
    // Optional input to control routing behavior for create new workflows
    public readonly useRouting = input<boolean>(true);

    private readonly queryParams = inject(ActivatedRoute).queryParams.pipe(distinctUntilChanged());
    private readonly queryParamsSignal = toSignal(this.queryParams);
    private observer?: IntersectionObserver;
    private isInitialScroll = true;
    private isDirectNavigation = false;
    private hasSyncedFromQueryParams = false;

    protected invalidSteps = signal<number[]>([]);
    protected activeStep = signal<number>(Number(this.route.snapshot.queryParams['step'] ?? 0));
    protected activeSection = signal<number>(Number(this.route.snapshot.queryParams['section'] ?? -1));
    protected steps = signal<Step[]>([]);
    protected isWorkflowFormDisabled = signal<boolean>(false);
    protected workflow = this.store.selectSignal<WorkflowData | undefined>(WorkflowDataState.selectedWorkflow);
    protected workflowConfig = this.store.selectSignal<WorkflowConfig>(WorkflowConfigState.selectedWorkflowConfig);
    protected isWorkflowConfigLoading = this.store.selectSignal<boolean>(WorkflowConfigState.isLoading);
    protected isWorkflowLoading = this.store.selectSignal<boolean>(WorkflowDataState.isLoading);
    protected isWorkflowConfigLoadingAndExisting = computed(
        () => this.isWorkflowConfigLoading() || !this.workflowConfig() || this.isWorkflowLoading()
    );
    protected hasExcessiveProducts = computed(() => this.workflow()?.hasExcessiveProducts ?? false);
    private readonly workflowDownloadProgress = this.store.selectSignal<WorkflowDownloadProgress | undefined>(
        WorkflowDataState.workflowDownloadProgress
    );
    protected readonly isLoadingExcelDownload = computed(() => {
        const id = this.workflow()?.id;
        const progress = this.workflowDownloadProgress();
        if (!id || !progress || progress.workflowId !== id) return false;
        return progress.status !== WorkflowDownloadStatus.COMPLETED && progress.status !== WorkflowDownloadStatus.ERROR;
    });
    protected isLoadingMoveToNextStep = signal<boolean>(false);
    protected transitionPhase = signal<WorkflowTransitionPhase>(WorkflowTransitionPhase.IDLE);
    protected readonly WorkflowTransitionPhase = WorkflowTransitionPhase;
    protected readonly isTransitioning = computed(
        () =>
            this.transitionPhase() !== WorkflowTransitionPhase.IDLE &&
            this.transitionPhase() !== WorkflowTransitionPhase.SAVE_FAILED &&
            this.transitionPhase() !== WorkflowTransitionPhase.VALIDATION_FAILED
    );
    public bgColor = input<string>('dark:bg-dark-bgColorPrimary bg-light-bgColorPrimary');

    protected stepRefs = viewChildren<ElementRef>('stepRef');
    protected sectionRefs = viewChildren<ElementRef>('sectionRef');
    protected stepperConfig = {
        fixedHeight: false
    };
    protected readonly workflowFormGroup = signal<FormGroup>(new FormGroup({}));
    protected readonly isWorkflowFormReady = signal<boolean>(false);
    public productsForm = computed(() => this.workflowFormGroup().controls['products'] as FormGroup);
    private elementsReady = computed(() => {
        const stepRefs = this.stepRefs();
        const sectionRefs = this.sectionRefs();
        const hasElements = stepRefs.length > 0 || sectionRefs.length > 0;
        const configLoaded = !this.isWorkflowConfigLoadingAndExisting();
        return hasElements && configLoaded;
    });
    protected filteredWorkflowConfigSteps = signal([]);

    private destroy$ = new Subject<boolean>();
    private clickedOnSubmit = signal(false);
    // Tracks whether the last user interaction was via keyboard (true) or pointer/touch (false)
    private lastInteractionIsKeyboard = false;
    private keydownHandler: (event: KeyboardEvent) => void;
    private pointerHandler: (event?: Event) => void;

    ngOnInit(): void {
        this.initializeWorkflowStepsLoading();
        this.subscribeToDOMReadyForIntersectionObserverSetup();
        this.subscribeToWorkflowChanges();
    }

    ngAfterViewInit(): void {
        if (this.useRouting()) {
            this.listenToTabChanges();
            this.listenToStepChanges();
            this.listenToSectionChanges();
            this.synchronizeStepStateWithQueryParams();
        }
        this.setupIntersectionObserver();
        // Setup global input modality listeners to detect keyboard vs pointer navigation
        this.keydownHandler = (event: KeyboardEvent) => {
            const key = event.key;
            if (key === 'Tab' || key.startsWith('Arrow') || key === 'Enter' || key === ' ') {
                this.lastInteractionIsKeyboard = true;
            }
        };
        this.pointerHandler = () => {
            this.lastInteractionIsKeyboard = false;
        };
        document.addEventListener('keydown', this.keydownHandler, true);
        document.addEventListener('pointerdown', this.pointerHandler, true);
        document.addEventListener('mousedown', this.pointerHandler, true);
        document.addEventListener('touchstart', this.pointerHandler, true);
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
        this.idleTimer.stop();
        this.navigationTimer.stop();
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
        this.workflowOperations.destroy();
        this.clickedOnSubmit.set(false);
        // Remove global input listeners
        if (this.keydownHandler) document.removeEventListener('keydown', this.keydownHandler, true);
        if (this.pointerHandler) {
            document.removeEventListener('pointerdown', this.pointerHandler, true);
            document.removeEventListener('mousedown', this.pointerHandler, true);
            document.removeEventListener('touchstart', this.pointerHandler, true);
        }
    }

    protected downloadExcelForWorkflow(): void {
        const workflowId = this.workflow()?.id;
        if (!workflowId || this.isLoadingExcelDownload()) {
            return;
        }
        this.store
            .dispatch(new StartWorkflowDownload(workflowId))
            .pipe(
                catchError(() =>
                    this.showMessage(
                        MessageOperationType.ERROR,
                        'workflow.error.workflow-download-failed.title',
                        'workflow.error.workflow-download-failed.description'
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    protected moveExcessiveWorkflowToNextStep(): void {
        const workflowId = this.workflow()?.id;
        const stepId = this.workflow()?.currentStepId;

        if (!workflowId || !stepId) {
            return;
        }

        this.isLoadingMoveToNextStep.set(true);
        this.transitionPhase.set(WorkflowTransitionPhase.VALIDATING);
        this.runAsyncValidationAndSendToNextStep(workflowId, stepId)
            .pipe(
                filter(Boolean),
                tap(() => {
                    this.transitionPhase.set(WorkflowTransitionPhase.SUCCESS);
                    this.idleTimer.start(() => this.transitionPhase.set(WorkflowTransitionPhase.IDLE));
                    this.showMessage(
                        MessageOperationType.SUCCESS,
                        'workflow.success.workflow-next-step.title',
                        'workflow.success.workflow-next-step.description'
                    );
                }),
                tap(() => {
                    if (this.useRouting()) {
                        void this.router.navigate(['workflows/details', workflowId], {
                            queryParams: { tab: 'dashboard' }
                        });
                    }
                }),
                catchError(() => {
                    this.transitionPhase.set(WorkflowTransitionPhase.VALIDATION_FAILED);
                    return this.showMessage(
                        MessageOperationType.ERROR,
                        'workflow.error.workflow-next-step-failed.title',
                        'workflow.error.workflow-next-step-failed.description'
                    );
                }),
                finalize(() => this.isLoadingMoveToNextStep.set(false)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    isFirstStep(): boolean {
        if (this.activeSection() !== -1) {
            return this.activeStep() === 0 && this.activeSection() === 0;
        }
        return this.activeStep() === 0;
    }

    isLastStep(): boolean {
        const currentStep = this.steps()[this.activeStep()];
        if (currentStep?.subSteps) {
            return (
                this.activeStep() === this.steps().length - 1 &&
                this.activeSection() === currentStep.subSteps.length - 1
            );
        } else {
            return this.activeStep() === this.steps().length - 1;
        }
    }

    public saveWorkflowChanges(): void {
        this.updateWorkflowData();
    }

    public sendWorkflowToPreviousStep(step: WorkflowConfigStep): Observable<boolean | void> {
        return this.store
            .dispatch(
                new SendWorkflowToNextStep(this.workflow().id, this.workflow().currentStepId, {
                    nextStepId: step.id
                })
            )
            .pipe(
                tap(() =>
                    this.showMessage(
                        MessageOperationType.SUCCESS,
                        'workflow.success.workflow-previous-step.title',
                        'workflow.success.workflow-previous-step.description'
                    )
                ),
                catchError(() =>
                    this.showMessage(
                        MessageOperationType.ERROR,
                        'workflow.error.workflow-previous-step-failed.title',
                        'workflow.error.workflow-previous-step-failed.description'
                    )
                )
            );
    }

    protected scrollToSection(index: number, type: SectionType): void {
        const refs = type === SectionType.MAIN ? this.stepRefs() : this.sectionRefs();

        // Get the current step to check if it has sections
        const currentStep = this.steps()[this.activeStep()];
        const hasSubSteps = currentStep?.subSteps && currentStep.subSteps.length > 0;
        let element: HTMLElement | undefined;
        if (type === SectionType.MAIN) {
            element = this.getStepOrFirstSectionOfStepIfPresentHTMLElement(hasSubSteps, element, refs);
        } else {
            element = refs.find(ref => ref.nativeElement.id === `section-${this.activeStep()}-${index}`)?.nativeElement;
        }
        element?.scrollIntoView({ behavior: 'smooth' });
    }

    protected setStep(step: number): void {
        this.isDirectNavigation = true;
        this.activeStep.set(step);
        this.activeSection.set(-1);
        this.scrollToSection(step, SectionType.MAIN);
        this.focusElement(`step-${step}`);
        setTimeout(() => {
            this.isDirectNavigation = false;
        }, 1000);
    }

    protected setSection(section: number): void {
        this.isDirectNavigation = true;
        this.activeSection.set(section);
        if (section >= 0) {
            this.scrollToSection(section, SectionType.SUB);
            this.focusElement(`section-${this.activeStep()}-${section}`);
        }
        setTimeout(() => {
            this.isDirectNavigation = false;
        }, 1000);
    }

    protected goToNext(): void {
        const sections = this.steps()[this.activeStep()].subSteps;

        if (sections && this.activeSection() < sections.length - 1) {
            this.setSection(this.activeSection() + 1);
        } else {
            this.setStep(Math.min(this.activeStep() + 1, this.steps().length - 1));
        }
    }

    protected goToPrevious(): void {
        const prevStep = Math.max(this.activeStep() - 1, 0);
        const prevSections = this.steps()[prevStep]?.subSteps;

        if (prevSections?.length) {
            this.activeStep.set(prevStep);
            const lastSection = prevSections.length - 1;
            this.activeSection.set(lastSection);
            this.setSection(lastSection);
        } else {
            this.setStep(prevStep);
        }
    }

    protected goToPreviousSection(): void {
        if (this.clickedOnSubmit()) {
            this.highlightInvalidStepperSteps();
        }
        if (this.activeSection() === 0) {
            this.activeSection.set(-1);
            this.scrollToSection(this.activeStep(), SectionType.MAIN);
        } else if (this.activeSection() > 0) {
            this.setSection(this.activeSection() - 1);
        } else {
            this.goToPrevious();
        }
    }

    protected goToNextSection(): void {
        const sections = this.steps()[this.activeStep()]?.subSteps ?? [];

        if (this.activeSection() === sections.length - 1 && this.activeStep() === this.steps().length - 1) {
            this.clickedOnSubmit.set(true);
            this.saveWorkflowData();
        }

        if (this.clickedOnSubmit()) {
            this.highlightInvalidStepperSteps();
        }
        if (this.activeSection() < sections.length - 1) {
            this.setSection(this.activeSection() + 1);
        } else {
            this.setStep(this.activeStep() + 1);
        }
    }

    private updateWorkflowData(sendToNextStep = false): void {
        const updateWorkflowData = this.workflowFormService.processWorkflowForm(
            WorkflowOperationType.UPDATE,
            this.workflowConfig(),
            this.workflowFormGroup(),
            this.workflow()
        ) as WorkflowData;
        const updateWorkflowDataToSave = {
            ...this.workflow(),
            ...updateWorkflowData
        };
        this.dispatchUpdateWorkflow(updateWorkflowDataToSave, sendToNextStep);
    }

    private dispatchUpdateWorkflow(updateWorkflowDataToSave: WorkflowData, sendToNextStep: boolean): void {
        const workflowId = this.workflow().id;
        const stepId = this.workflow().currentStepId;

        if (sendToNextStep) {
            this.transitionPhase.set(WorkflowTransitionPhase.SAVING);
            this.workflowFormGroup().disable();
        }

        this.store
            .dispatch(new UpdateWorkflowData(updateWorkflowDataToSave))
            .pipe(
                tap(() => {
                    if (!sendToNextStep) {
                        this.showMessage(
                            MessageOperationType.SUCCESS,
                            'workflow.success.workflow-updated.title',
                            'workflow.success.workflow-updated.description'
                        );
                    }
                }),
                switchMap(() => {
                    if (sendToNextStep) {
                        this.transitionPhase.set(WorkflowTransitionPhase.VALIDATING);
                        return this.runAsyncValidationAndSendToNextStep(workflowId, stepId);
                    }
                    return of(false);
                }),
                filter((didSendToNextStep): didSendToNextStep is true => didSendToNextStep === true),
                tap(() => {
                    this.transitionPhase.set(WorkflowTransitionPhase.SUCCESS);
                    this.idleTimer.start(() => this.transitionPhase.set(WorkflowTransitionPhase.IDLE));
                    this.showMessage(
                        MessageOperationType.SUCCESS,
                        'workflow.success.workflow-next-step.title',
                        'workflow.success.workflow-next-step.description'
                    );
                }),
                tap(() => {
                    if (this.useRouting()) {
                        this.navigationTimer.start(
                            () =>
                                void this.router.navigate(['workflows/details', updateWorkflowDataToSave.id], {
                                    queryParams: { tab: 'dashboard' }
                                })
                        );
                    }
                }),
                catchError(_error => {
                    if (sendToNextStep) {
                        if (this.transitionPhase() === WorkflowTransitionPhase.SAVING) {
                            this.transitionPhase.set(WorkflowTransitionPhase.SAVE_FAILED);
                        } else {
                            this.transitionPhase.set(WorkflowTransitionPhase.VALIDATION_FAILED);
                        }
                    }

                    const messages = this.extractBackendValidationMessages(_error);
                    if (messages.length > 0) {
                        this.workflowFormService.applyBackendValidationMessages(
                            messages,
                            this.workflowConfig(),
                            this.workflowFormGroup()
                        );
                        this.highlightInvalidStepperSteps();
                        this.showRequiredFieldsMissingErrorMessageAndScrollToErrorStep();
                        return of(null);
                    }

                    return this.showMessage(
                        MessageOperationType.ERROR,
                        sendToNextStep
                            ? 'workflow.error.workflow-next-step-failed.title'
                            : 'workflow.error.workflow-update-failed.title',
                        sendToNextStep
                            ? 'workflow.error.workflow-next-step-failed.description'
                            : 'workflow.error.workflow-update-failed.description'
                    );
                }),
                finalize(() => {
                    if (sendToNextStep) {
                        this.workflowFormGroup().enable();
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    /**
     * Runs an async validation job (parallel SFN-backed) and, on success,
     * dispatches SendWorkflowToNextStep with the cached jobId so the API gw
     * doesn't re-validate. Emits true when the next-step transition was sent,
     * false when validation produced messages and we stopped before
     * transitioning. Errors bubble to the outer catchError.
     */
    private runAsyncValidationAndSendToNextStep(workflowId: string, stepId: string): Observable<boolean> {
        return this.store.dispatch(new StartValidationJob(workflowId)).pipe(
            switchMap(() => {
                const job = this.store.selectSnapshot(WorkflowDataState.validationJob);
                if (!job || job.status !== WorkflowValidationStatus.SUCCEEDED) {
                    throw new Error(job?.errorMessage ?? 'Workflow validation failed.');
                }
                if (!job.valid) {
                    this.transitionPhase.set(WorkflowTransitionPhase.VALIDATION_FAILED);
                    const messages = job.messages ?? [];
                    this.workflowFormService.applyBackendValidationMessages(
                        messages,
                        this.workflowConfig(),
                        this.workflowFormGroup()
                    );
                    this.highlightInvalidStepperSteps();
                    this.showAsyncValidationFailedMessage(messages);
                    this.store.dispatch(new ClearValidationJob());
                    return of(false);
                }
                return this.store
                    .dispatch(new SendWorkflowToNextStep(workflowId, stepId, { validationJobId: job.jobId }))
                    .pipe(
                        tap(() => this.store.dispatch(new ClearValidationJob())),
                        map(() => true)
                    );
            })
        );
    }

    private showAsyncValidationFailedMessage(messages: string[]): void {
        const baseDescription = this.translateService.instant('workflow.error.async-validation-failed.description');
        const message = messages.length > 0 ? `${baseDescription}:\n${messages.join('\n')}` : baseDescription;
        this.toastService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('workflow.error.async-validation-failed.title'),
            message,
            timeout: 15000
        });
    }

    private saveWorkflowData(): void {
        if (this.workflow()) {
            this.validateUpdateAndSendWorkflowToNextStep();
        } else {
            this.validateAndCreateWorkflow();
        }
    }

    private validateAndCreateWorkflow(): void {
        if (
            this.workflowFormService.validateWorkflowForm(
                WorkflowOperationType.CREATE,
                this.workflowFormGroup(),
                this.filteredWorkflowConfigSteps()
            )
        ) {
            const createWorkflowData = this.workflowFormService.processWorkflowForm(
                WorkflowOperationType.CREATE,
                this.workflowConfig(),
                this.workflowFormGroup()
            ) as CreateWorkflowData;
            const hasSnowflakeParams =
                (createWorkflowData.gck || createWorkflowData.pck || createWorkflowData.priceGroup) &&
                createWorkflowData.coreMlfbs !== undefined &&
                createWorkflowData.coreMlfbs.length > 0;
            const createAction = hasSnowflakeParams
                ? new CreateNewWorkflowDataFromSapSearch(createWorkflowData)
                : new CreateNewWorkflowData(createWorkflowData);
            this.store
                .dispatch(createAction)
                .pipe(
                    tap(() => this.showCreateWorkflowSuccessAndNavigateToWorkflow()),
                    catchError(() =>
                        this.showMessage(
                            MessageOperationType.ERROR,
                            'workflow.error.workflow-create-failed.title',
                            'workflow.error.workflow-create-failed.description'
                        )
                    ),
                    takeUntil(this.destroy$)
                )
                .subscribe();
        } else {
            this.showRequiredFieldsMissingErrorMessageAndScrollToErrorStep();
        }
    }

    private highlightInvalidStepperSteps(): void {
        const errors = this.workflowFormService.findErrorLocations(this.workflowFormGroup(), this.workflowConfig());

        if (!errors || errors.length === 0) {
            this.invalidSteps.set([]);
            const updatedSteps = [...this.steps()];

            updatedSteps.forEach(step => {
                if (step.subSteps) {
                    step.subSteps.forEach(subStep => {
                        subStep.invalid = false;
                    });
                }
            });
            return;
        }

        const invalidStepIndices = [...new Set(errors.map(error => error.stepIndex))];
        this.invalidSteps.set(invalidStepIndices);

        const updatedSteps = [...this.steps()];

        updatedSteps.forEach(step => {
            if (step.subSteps) {
                step.subSteps.forEach(subStep => {
                    subStep.invalid = false;
                });
            }
        });

        errors.forEach(error => {
            const { stepIndex, sectionIndex } = error;

            if (stepIndex >= 0 && stepIndex < updatedSteps.length) {
                if (sectionIndex !== undefined && sectionIndex !== null) {
                    const step = updatedSteps[stepIndex];
                    if (step.subSteps && sectionIndex >= 0 && sectionIndex < step.subSteps.length) {
                        step.subSteps[sectionIndex].invalid = true;
                    }
                }
            }
        });

        this.steps.set(updatedSteps);
    }

    private showRequiredFieldsMissingErrorMessageAndScrollToErrorStep(): void {
        const errors = this.workflowFormService.findErrorLocations(this.workflowFormGroup(), this.workflowConfig());
        if (errors.length > 0) {
            this.setStep(errors[0].stepIndex);
            this.setSection(errors[0].sectionIndex - 1);
            this.highlightInvalidStepperSteps();
        }
        const errorStepIndex = errors[0].stepIndex;
        const invalidFields = this.workflowFormService.findInvalidFieldLabels(
            this.workflowFormGroup(),
            this.workflowConfig(),
            errorStepIndex
        );
        const baseDescription = this.translateService.instant('workflow.error.invalid-or-missing-fields.description');
        const fieldLines = invalidFields.map(({ label, errors }) => {
            const translatedLabel = this.translateService.instant(label);
            const translatedErrors = Object.entries(errors)
                .map(([key, val]) =>
                    key === 'backendError' ? (val as string) : this.translateService.instant(`validation.${key}`)
                )
                .filter(t => t && !t.startsWith('validation.'))
                .join(', ');
            return translatedErrors ? `${translatedLabel} (${translatedErrors})` : translatedLabel;
        });
        const description = fieldLines.length > 0 ? `${baseDescription}:\n${fieldLines.join('\n')}` : baseDescription;
        this.snackbarService.open(
            CreateWorkflowOperations.getSnackbarMessageConfig(
                MessageOperationType.ERROR,
                'workflow.error.invalid-or-missing-fields.title',
                description,
                this.translateService
            )
        );
    }

    private extractBackendValidationMessages(error: unknown): string[] {
        if (!(error instanceof HttpErrorResponse) || error.status !== 400) return [];
        const body = error.error as { message?: unknown } | null;
        return Array.isArray(body?.message) ? (body.message as string[]) : [];
    }

    private validateUpdateAndSendWorkflowToNextStep(): void {
        const currentStepConfig = this.filteredWorkflowConfigSteps().find(
            step => step.id === this.workflow()?.currentStepId
        );
        const stepsToValidate = currentStepConfig ? [currentStepConfig] : this.filteredWorkflowConfigSteps();
        if (
            this.workflowFormService.validateWorkflowForm(
                WorkflowOperationType.UPDATE,
                this.workflowFormGroup(),
                stepsToValidate
            )
        ) {
            this.updateWorkflowData(true);
        } else {
            this.showRequiredFieldsMissingErrorMessageAndScrollToErrorStep();
        }
    }

    private initializeWorkflowStepsLoading(): void {
        toObservable(this.workflowConfig, { injector: this.injector })
            .pipe(
                filter(workflowConfig => !!workflowConfig),
                tap(() => this.initializeSteps()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private subscribeToWorkflowChanges(): void {
        toObservable(this.workflow, { injector: this.injector })
            .pipe(
                filter(workflow => !!workflow),
                tap(() => this.initializeSteps()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private initializeSteps(): void {
        if (this.workflowConfig()) {
            this.initializeStepperSteps();
            this.initializeWorkflowForms();
        }
    }

    private initializeStepperSteps() {
        const steps = this.workflowConfig()?.steps || [];
        let filteredSteps = steps;
        if (this.workflow()) {
            const isAssignedToCurrentUser = this.authService.userIdTokenClaims()?.oid === this.workflow().assignedTo;
            const canEdit = this.isAdmin() || this.isCreator() || isAssignedToCurrentUser;
            this.isWorkflowFormDisabled.set(!canEdit);
            const currentStepIndex = steps.findIndex(step => step.id === this.workflow().currentStepId);
            if (currentStepIndex !== -1) {
                // Only include steps up to and including the current step
                filteredSteps = steps.slice(0, currentStepIndex + 1);
            }
        }

        this.steps.set(
            filteredSteps
                .filter(step => this.useRouting() || step.shownInCreate)
                .map(step => ({
                    label: this.translateService.instant(step.name),
                    subSteps: step.sections
                        .filter(section => this.useRouting() || section.shownInCreate)
                        .map(section => ({
                            label: this.translateService.instant(section.name)
                        }))
                }))
        );
        this.filteredWorkflowConfigSteps.set(
            filteredSteps
                .filter(step => this.useRouting() || step.shownInCreate)
                .map(step => ({
                    ...step,
                    sections: step.sections.filter(section => this.useRouting() || section.shownInCreate)
                }))
        );
    }

    protected isActiveStep(stepName: string): boolean {
        const currentStep = this.workflowConfig()?.steps.find(step => step.name === stepName);
        return this.workflow()?.currentStepId === currentStep?.id;
    }

    protected isAdmin(): boolean {
        return this.authService.userIdTokenClaims()?.roles.includes(UserRole.ADMIN);
    }

    protected isCreator(): boolean {
        return this.authService.userIdTokenClaims()?.oid === this.workflow().createdBy;
    }

    private listenToTabChanges(): void {
        this.route.queryParams
            .pipe(
                pairwise(),
                tap(([prev, curr]) => {
                    if (prev['tab'] !== curr['tab'] && curr['tab'] === 'workflow') {
                        this.hasSyncedFromQueryParams = false;
                        setTimeout(() => {
                            this.updateActiveSection();
                            this.subscribeToDOMReadyForIntersectionObserverSetup();
                        }, 100);
                    }

                    if (prev['tab'] === 'workflow' && curr['tab'] !== 'workflow') {
                        this.hasSyncedFromQueryParams = false;
                        if (this.observer) {
                            this.observer.disconnect();
                            this.observer = undefined;
                        }
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private listenToStepChanges(): void {
        this.route.queryParams
            .pipe(
                pairwise(),
                filter(([_, curr]) => curr['step'] && Number(this.activeStep()) !== Number(curr['step'])),
                tap(([, curr]) => this.setStep(Number(curr['step']))),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private listenToSectionChanges(): void {
        this.route.queryParams
            .pipe(
                pairwise(),
                filter(
                    ([prev, curr]) =>
                        curr['tab'] === 'workflow' &&
                        Number(prev['step']) === Number(curr['step']) &&
                        Number(this.activeSection()) !== Number(curr['section'])
                ),
                tap(([, curr]) => this.setSection(Number(curr['section']))),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private synchronizeStepStateWithQueryParams(): void {
        const shouldSync = computed(() => {
            const params = this.queryParamsSignal();
            const isWorkflowTab = params?.['tab'] === 'workflow';
            const step = this.activeStep();
            const section = this.activeSection();
            return { isWorkflowTab, step, section, params };
        });

        toObservable(shouldSync, { injector: this.injector })
            .pipe(
                filter(({ isWorkflowTab }) => isWorkflowTab),
                tap(({ step, section, params }) =>
                    this.syncStepStateWithQueryParams(step, section, params as Record<string, unknown> | undefined)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private syncStepStateWithQueryParams(
        step: number,
        section: number,
        params: Record<string, unknown> | undefined
    ): void {
        const hasStepParam = params?.['step'] !== undefined;
        const hasSectionParam = params?.['section'] !== undefined;
        const routeStep = hasStepParam ? Number(params?.['step']) : undefined;
        const routeSection = hasSectionParam ? Number(params?.['section']) : undefined;

        // On first activation of the workflow tab, hydrate local state from URL first.
        if (!this.hasSyncedFromQueryParams && hasStepParam && hasSectionParam) {
            this.hasSyncedFromQueryParams = true;
            if (!Number.isNaN(routeStep)) {
                this.activeStep.set(routeStep);
            }
            if (!Number.isNaN(routeSection)) {
                this.activeSection.set(routeSection);
            }
            return;
        }

        if (!this.hasSyncedFromQueryParams) {
            this.hasSyncedFromQueryParams = true;
        }

        const routeDiffersFromLocal = routeStep !== step || routeSection !== section;

        if (!this.isDirectNavigation && hasStepParam && hasSectionParam && routeDiffersFromLocal) {
            if (!Number.isNaN(routeStep)) {
                this.activeStep.set(routeStep);
            }
            if (!Number.isNaN(routeSection)) {
                this.activeSection.set(routeSection);
            }
            return;
        }

        if (!routeDiffersFromLocal) {
            return;
        }

        if (!this.isDirectNavigation && hasStepParam && hasSectionParam) {
            return;
        }

        void this.router.navigate([], {
            queryParams: { step, section },
            queryParamsHandling: 'merge'
        });
    }

    private setupIntersectionObserver(): void {
        setTimeout(() => {
            const step = Number(this.route.snapshot.queryParams['step'] ?? 0);
            const section = Number(this.route.snapshot.queryParams['section'] ?? -1);

            if (section !== -1) {
                this.scrollToSection(section, SectionType.SUB);
            } else {
                this.scrollToSection(step, SectionType.MAIN);
            }

            setTimeout(() => {
                this.isInitialScroll = false;
                this.setupObserver();
            }, 100);
        });
    }

    private setupObserver(): void {
        const options = {
            root: document.querySelector('.flex-1.ml-8.overflow-auto'),
            rootMargin: '-50% 0px',
            threshold: 0
        };

        this.observer = new IntersectionObserver(entries => {
            if (this.isInitialScroll || this.isDirectNavigation) return;

            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.scrollIntoView(entry.target);
                }
            });
        }, options);

        this.stepRefs().forEach(ref => {
            this.observer?.observe(ref.nativeElement);
        });
        this.sectionRefs().forEach(ref => {
            this.observer?.observe(ref.nativeElement);
        });
    }

    private scrollIntoView(element: Element): void {
        if (element.id.startsWith('step-')) {
            const stepIndex = parseInt(element.id.split('-')[1]);
            this.activeStep.set(stepIndex);
            this.activeSection.set(-1);
            this.focusElement(element.id);
        } else if (element.id.startsWith('section-')) {
            const [, stepIndex, sectionIndex] = element.id.split('-');
            this.activeStep.set(parseInt(stepIndex));
            this.activeSection.set(parseInt(sectionIndex));
            this.focusElement(element.id);
        }
    }

    private focusElement(elementId: string): void {
        setTimeout(() => {
            if (!this.lastInteractionIsKeyboard) return;

            const element = document.getElementById(elementId);
            if (element) {
                element.tabIndex = -1;
                element.focus({ preventScroll: true });
            }
        }, 100);
    }

    private updateActiveSection(): void {
        if (!this.route.snapshot.queryParams['step'] || !this.route.snapshot.queryParams['section']) {
            this.setStep(this.activeStep());
            this.setSection(this.activeSection());
        } else {
            this.scrollToSection(
                // this.activeSection() !== -1 ? this.activeSection() : this.activeStep(),
                // this.activeSection() !== -1 ? SectionType.SUB : SectionType.MAIN
                this.route.snapshot.queryParams['step'],
                SectionType.MAIN
            );
        }
    }

    private subscribeToDOMReadyForIntersectionObserverSetup(): void {
        toObservable(this.elementsReady, { injector: this.injector })
            .pipe(
                tap(() => {
                    if (this.elementsReady()) {
                        // Clean up existing observer
                        if (this.observer) {
                            this.observer.disconnect();
                        }
                        // Setup new observer with current elements
                        this.setupIntersectionObserver();
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private initializeWorkflowForms(): void {
        const workflowFormGroup = this.workflowFormService.createWorkflowForm(this.workflowConfig(), this.workflow());
        const stepNames = Object.keys((workflowFormGroup.get('steps') as FormGroup).controls);
        const activeStepIndex = this.workflowConfig()?.steps.findIndex(
            step => step.id === this.workflow()?.currentStepId
        );
        stepNames.forEach((stepName, index) => {
            const stepControl = workflowFormGroup.get(`steps.${stepName}`) as FormGroup;
            if (index < activeStepIndex && !this.isAdmin() && !this.isCreator()) {
                stepControl.disable();
            }
        });
        if (this.isWorkflowFormDisabled()) {
            workflowFormGroup.disable();
        }
        this.workflowFormGroup.set(workflowFormGroup);
        this.isWorkflowFormReady.set(true);
        this.setupDependentFieldSubscription();
        if (!this.useRouting()) {
            this.setupEndDateValidationSubscription();
        }
        this.setupCoreMlfbFormatting();
    }

    private showCreateWorkflowSuccessAndNavigateToWorkflow(): void {
        this.showMessage(
            MessageOperationType.SUCCESS,
            'workflow.success.workflow-created.title',
            'workflow.success.workflow-created.description'
        );
        // if useRouting is false, navigate to the workflow details page, because this is the create-workflow dialog
        if (!this.useRouting()) {
            this.dialogService.closeAll();
            void this.router.navigate(['workflows/details', this.workflow().id]);
        }
    }

    private getStepOrFirstSectionOfStepIfPresentHTMLElement(
        hasSubSteps: boolean,
        element: HTMLElement,
        refs: ReadonlyArray<ElementRef>
    ): HTMLElement {
        if (hasSubSteps) {
            element = this.sectionRefs().find(
                ref => ref.nativeElement.id === `section-${this.activeStep()}-0`
            )?.nativeElement;
            if (element) {
                this.activeSection.set(0);
            }
        }

        if (!element) {
            element = refs.find(ref => ref.nativeElement.id === `step-${this.activeStep()}`)?.nativeElement;
        }
        return element;
    }

    private showMessage(operationType: MessageOperationType, title: string, description: string): Observable<boolean> {
        this.snackbarService.open(
            CreateWorkflowOperations.getSnackbarMessageConfig(operationType, title, description, this.translateService)
        );
        return of(false);
    }

    private setupDependentFieldSubscription(): void {
        if (this.workflow()) {
            this.workflowOperations.setupDependentFieldSubscriptions(
                this.workflowFormGroup(),
                this.workflowConfig(),
                this.filteredWorkflowConfigSteps(),
                this.workflow(),
                this.destroy$,
                this.isAdmin(),
                updater => this.filteredWorkflowConfigSteps.update(updater)
            );
        }
    }

    private setupEndDateValidationSubscription(): void {
        const section = this.workflowFormGroup().get(
            'steps.step-step-setup.sections.section-section-basic-setup'
        ) as FormGroup;

        const startDate = section.get('start-date');
        const endDate = section.get('end-date');

        endDate.setValidators([
            endDate.validator,
            (control: AbstractControl): ValidationErrors | null => {
                if (!startDate?.value || !control.value) return null;

                const start = new Date(startDate.value).setHours(0, 0, 0, 0);
                const end = new Date(control.value).setHours(0, 0, 0, 0);

                return end <= start ? { 'end-date-before-start-date': true } : null;
            }
        ]);

        startDate.valueChanges
            .pipe(
                tap(() => endDate.updateValueAndValidity()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private setupCoreMlfbFormatting(): void {
        const section = this.workflowFormGroup().get(
            'steps.step-step-setup.sections.section-section-basic-setup'
        ) as FormGroup;

        const coreMlfbControl = section.get('core-mlfb');

        coreMlfbControl.valueChanges
            .pipe(
                tap((value: string) => {
                    if (!value) return;

                    // Normalize comma-separated values: trim spaces around commas and remove duplicates
                    const formatted = value
                        .split(',')
                        .map(item => item.trim())
                        .filter(item => item.length > 0)
                        .join(', ');

                    if (formatted !== value) {
                        coreMlfbControl.setValue(formatted, { emitEvent: false });
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }
}
